import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Checkbox,
  cn,
  Input,
  Label,
  Switch,
  Textarea,
} from '@design-system';
import {
  AdditionalSettingsButton,
  EmailTagInput,
  useInviteClientStore,
} from '@/features/invite-client';

import { AlertTriangle, Check, Loader2, Pencil, Send, X } from 'lucide-react';

import { useState } from 'react';

interface EmailTabProps {
  showHeader?: boolean;
  showAdditionalSettings?: boolean;
  showBulkMode?: boolean;
}

export function EmailTab({
  showHeader = true,
  showAdditionalSettings = true,
  showBulkMode = true,
}: EmailTabProps) {
  const {
    inviteClientConfig,
    isEmailValid,
    isAdvisorEmail,
    canContinueSingle,
    validBulkEmails,
    canContinueBulk,
    isLoading,
    handleSendEmail,
    handleConfigChange,
    handleClearBulkEmails,
  } = useInviteClientStore();

  const [isEditing, setIsEditing] = useState(false);

  const { email, firstName, lastName, emailBody, isBulkMode, bulkEmails, isComplianceConfirmed } =
    inviteClientConfig;

  const canContinue = isBulkMode ? canContinueBulk : canContinueSingle;

  return (
    <div>
      {showHeader && (
        <div>
          <h4 className="text-base font-semibold text-gray-950">Send an email invitation</h4>
          <p className="text-foreground text-sm">
            We'll send a proven email template so you don't have to write one from scratch.
          </p>
        </div>
      )}

      <fieldset
        disabled={isLoading}
        className="mb-6 mt-4"
      >
        <div className="space-y-6">
          {showBulkMode && (
            <label
              htmlFor="invite-multiple-clients"
              className="bg-muted flex w-full cursor-pointer items-center justify-between rounded-lg border p-3"
            >
              <span className="text-foreground text-sm font-medium">Invite Multiple Clients</span>
              <Switch
                id="invite-multiple-clients"
                checked={isBulkMode}
                onCheckedChange={checked => handleConfigChange('isBulkMode', !!checked)}
              />
            </label>
          )}

          {isBulkMode && showBulkMode ? (
            <div className="space-y-2">
              <EmailTagInput
                value={bulkEmails}
                onChange={value => handleConfigChange('bulkEmails', value)}
                maxEmails={35}
              />
              {bulkEmails.length > 0 && (
                <div className="flex justify-end">
                  <Badge
                    variant="destructive"
                    className="cursor-pointer"
                    onClick={handleClearBulkEmails}
                  >
                    <X className="size-3" />
                    <span className="pr-1">Clear all</span>
                  </Badge>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="firstName">First name (optional)</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Jane"
                    value={firstName}
                    onChange={({ target }) => handleConfigChange('firstName', target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="lastName">Last name (optional)</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={({ target }) => handleConfigChange('lastName', target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  name="client-email"
                  autoComplete="email"
                  placeholder="client@example.com"
                  value={email}
                  onChange={({ target }) => handleConfigChange('email', target.value)}
                  className={cn({
                    'text-destructive': email.length > 0 && !isEmailValid,
                    'border-yellow-600': isAdvisorEmail,
                  })}
                />
                {isAdvisorEmail && (
                  <Alert variant="warning">
                    <AlertTriangle />
                    <AlertDescription>
                      This email is already associated with your account. Each Waterlily account
                      needs a unique email, so try using a personal email instead.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="cursor-default">Email template</Label>
              <Badge
                className="cursor-pointer"
                onClick={() => !isLoading && setIsEditing(!isEditing)}
              >
                {isEditing ? (
                  <>
                    <Check className="size-3" />
                    <span className="pr-1">Save</span>
                  </>
                ) : (
                  <>
                    <Pencil className="size-3" />
                    <span className="pr-1">Edit</span>
                  </>
                )}
              </Badge>
            </div>
            {isEditing ? (
              <Textarea
                value={emailBody}
                onChange={({ target }) => handleConfigChange('emailBody', target.value)}
                rows={6}
                className="text-sm"
              />
            ) : (
              <div className="bg-muted rounded-lg border p-3">
                <p className="text-foreground line-clamp-3 text-sm">"{emailBody}"</p>
              </div>
            )}
          </div>

          {isBulkMode && showBulkMode && (
            <div className="flex items-start gap-3">
              <Checkbox
                id="compliance"
                checked={isComplianceConfirmed}
                onCheckedChange={checked => handleConfigChange('isComplianceConfirmed', !!checked)}
                className="translate-y-0.5"
              />
              <Label
                htmlFor="compliance"
                className="text-foreground text-sm italic"
              >
                I confirm that I have either obtained compliance approval or understand this message
                may require compliance review. I acknowledge that sending this email is my
                responsibility.
              </Label>
            </div>
          )}
        </div>
      </fieldset>

      <div className="flex gap-3">
        {showAdditionalSettings && <AdditionalSettingsButton className="flex-1" />}
        <Button
          className={showAdditionalSettings ? 'flex-1' : 'w-full'}
          onClick={() => handleSendEmail(isBulkMode)}
          disabled={!canContinue || isLoading}
        >
          {isLoading ? <Loader2 className="animate-spin" /> : <Send />}
          {isLoading
            ? 'Sending...'
            : isBulkMode
              ? `Send ${validBulkEmails.length} invitation${validBulkEmails.length !== 1 ? 's' : ''}`
              : 'Send invitation'}
        </Button>
      </div>
    </div>
  );
}
